<template>
    <b-modal
      v-model="data.showModal"
      size="xxl"
      button-size="sm"
      modal-class="dropdown-purchases-editor"
      title-class="w-full"
      footer-class="w-full"
      no-fade
      @show="onShow"
      no-close-on-esc
      no-close-on-backdrop
      scrollable
    >
      <!-- Modal header -->
      <div slot="modal-title">
        <div class="flex w-full justify-between">
          <div class="flex gap-1">
            <div>Edit purchases</div>
            <div v-if="group">- {{ group.group_name }}</div>
          </div>
        </div>
      </div>

      <!-- Modal content -->
      <div v-if="group && groupOptions && purchases" class="flex flex-col gap-4">
        <div class="flex flex-col">
          <table class="table table-google table-highlight">
            <thead class="sticky z-10" style="top: -1rem;">
              <tr>
                <th class="text-nowrap">Option</th>
                <th class="text-nowrap">Date</th>
                <th class="text-nowrap">Price</th>
                <th class="text-nowrap">Currency</th>
                <th class="text-nowrap">Rate</th>
                <th class="text-nowrap">Price (CZK)</th>
                <th class="text-nowrap">Unit Type</th>
                <th class="text-nowrap">Unit</th>
                <th class="text-nowrap">Quantity</th>
                <th><!-- Actions --></th>
                <th><!-- Filler --></th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="purchases.length === 0">
                <td colspan="11">No purchases available</td>
              </tr>

              <tr v-for="purchase in purchases" :key="purchase.guid">
                <td class="row-fit">
                  <v-select
                    v-model="purchase.option_id"
                    :options="optionOptions"
                    label="text"
                    :reduce="option => option.value"
                    placeholder="Select option"
                    class="w-56"
                    appendToBody
                  />
                </td>
                <td class="row-fit">
                  <date-picker
                    class="date-picker"
                    v-model="purchase.purchase_date"
                    format="DD.MM.YYYY"
                    valueType="YYYY-MM-DD"
                    :clearable="false"
                    :lang="datepickerLocale"
                    @input="fetchRate(purchase)"
                  />
                </td>
                <td class="row-fit">
                  <b-form-input
                    v-model="purchase.purchase_price"
                    class="w-32 text-right"
                    placeholder="Price"
                    size="sm"
                    trim
                    autocomplete="off"
                  ></b-form-input>
                </td>
                <td class="row-fit">
                  <b-form-select
                    v-model="purchase.currency_id"
                    :options="options.currencies"
                    size="sm"
                    class="w-24"
                    @change="fetchRate(purchase)"
                  ></b-form-select>
                </td>
                <td class="row-fit text-nowrap text-right">
                  <span v-if="purchase.currency_rate !== null">
                    {{ roundNumber(purchase.currency_rate, 2, 6) }}
                  </span>
                </td>
                <td class="row-fit text-nowrap text-right">
                  {{ getPurchaseCzk(purchase) }}
                </td>
                <td class="row-fit">
                  <b-form-select
                    v-model="purchase.unit_type"
                    :options="options.unit_type"
                    size="sm"
                    class="w-24"
                    @change="onUnitType(purchase)"
                  ></b-form-select>
                </td>
                <td class="row-fit">
                  <b-form-select
                    v-model="purchase.unit_code"
                    :options="getUnitOptions(purchase.unit_type)"
                    size="sm"
                    class="w-24"
                  ></b-form-select>
                </td>
                <td class="row-fit">
                  <b-form-input
                    v-model="purchase.unit_value"
                    class="w-24 text-right"
                    placeholder="Quantity"
                    size="sm"
                    trim
                    autocomplete="off"
                  ></b-form-input>
                </td>
                <td class="row-fit">
                  <b-btn
                    size="sm"
                    variant="danger"
                    :disabled="isLoading"
                    @click="deletePurchase(purchase)"
                    title="Delete purchase"
                  >
                    &times;
                  </b-btn>
                </td>
                <td class="w-100"><!-- Filler --></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-else>
        <div v-if="isLoading">Loading..</div>
        <div v-else>Error</div>
      </div>

      <!-- Modal footer -->
      <div slot="modal-footer" class="w-full d-flex gap-4 justify-content-between items-center">
        <div class="flex w-full justify-content-between items-center">
          <div class="flex gap-4">
            <b-btn
              size="sm"
              variant="primary"
              :disabled="isLoading || !canSave"
              @click="onSave"
            >
              Save
            </b-btn>

            <b-btn
              size="sm"
              variant="primary"
              :disabled="isLoading || purchases === null"
              @click="createPurchase"
            >
              Add new purchase
            </b-btn>
          </div>
        </div>
      </div>
    </b-modal>
  </template>

<script>
import {
  datepickerLocale,
  generateGuid,
  getUnitTypeOptions,
  numberToString,
  productUnits,
  roundNumber,
  stringToNumber,
} from '@/helpers';

export default {
  name: 'DropdownPurchasesEditor',
  props: {
    data: Object,
  },
  computed: {
    datepickerLocale() {
      return datepickerLocale;
    },
    optionOptions() {
      if (!this.group || !this.groupOptions) {
        return [];
      }
      return this.groupOptions.map((option) => ({
        text: `${this.group.option_prefix}${option.option_value}${this.group.option_suffix}`,
        value: option.id,
      }));
    },
    isLoading() {
      return this.loadingCount > 0;
    },
    canSave() {
      if (this.purchases === null) return false;
      return this.purchases.every((purchase) => this.isPurchaseValid(purchase));
    },
    sendPurchases() {
      if (this.purchases === null) {
        return null;
      }
      const sendPurchases = this.purchases.map((purchase) => ({
        id: purchase.id,
        option_id: purchase.option_id,
        currency_id: purchase.currency_id,
        currency_rate: purchase.currency_rate,
        purchase_date: purchase.purchase_date,
        purchase_price: stringToNumber(purchase.purchase_price),
        purchase_price_czk: stringToNumber(purchase.purchase_price) * purchase.currency_rate,
        unit_type: purchase.unit_type,
        unit_code: purchase.unit_code,
        unit_value: stringToNumber(purchase.unit_value),
      }));
      return sendPurchases;
    },
  },
  data() {
    return {
      loadingCount: 0,
      options: {
        unit_type: getUnitTypeOptions(),
        currencies: this.$store.state.app.currencies.map((currency) => ({
          text: currency.currency_code,
          value: currency.id,
        })),
      },
      units: productUnits,
      group: null,
      groupOptions: null,
      purchases: null,
    };
  },
  methods: {
    roundNumber(value, minimumFractionDigits, maximumFractionDigits) {
      return roundNumber(value, minimumFractionDigits, maximumFractionDigits);
    },
    isPurchaseValid(purchase) {
      if (purchase.option_id === null) return false;
      if (purchase.purchase_date === null || purchase.purchase_date.length === 0) return false;
      if (purchase.currency_rate === null || isNaN(purchase.currency_rate)) return false;
      if (purchase.purchase_price.length === 0) return false;
      if (isNaN(stringToNumber(purchase.purchase_price))) return false;
      if (purchase.unit_value.length === 0) return false;
      if (isNaN(stringToNumber(purchase.unit_value))) return false;
      return true;
    },
    getPurchaseCzk(purchase) {
      if (!this.isPurchaseValid(purchase)) {
        return '';
      }
      const purchaseCzk = stringToNumber(purchase.purchase_price) * purchase.currency_rate;
      return roundNumber(purchaseCzk, 2, 2);
    },
    getUnitOptions(unitType) {
      return Object.keys(this.units[unitType])
        .map((unit) => ({ text: unit, value: unit }));
    },
    onUnitType(purchase) {
      const defaultUnit = Object.entries(this.units[purchase.unit_type])
        .find((unit) => unit[1] === 1)[0];
      purchase.unit_code = defaultUnit;
    },
    getToday() {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${now.getFullYear()}-${month}-${day}`;
    },
    fetchGroup() {
      this.loadingCount++;
      this.$http
        .get(`/select/groups/${this.data.groupKey}`)
        .then((res) => {
          this.group = res.body;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch group: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchGroupOptions() {
      this.loadingCount++;
      this.$http
        .get(`/select/group_options/${this.data.groupKey}`)
        .then((res) => {
          this.groupOptions = res.body;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch options: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchPurchases() {
      this.loadingCount++;
      this.$http
        .get(`/select/group_purchases/${this.data.groupKey}`)
        .then((res) => {
          res.body.forEach((purchase) => {
            purchase.guid = generateGuid();
            purchase.purchase_price = numberToString(purchase.purchase_price);
            purchase.unit_value = numberToString(purchase.unit_value);
          });
          this.purchases = res.body;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch purchases: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchRate(purchase) {
      if (purchase.purchase_date === null || purchase.purchase_date.length === 0) {
        return;
      }
      purchase.currency_rate = null;
      this.loadingCount++;
      this.$http
        .get(`/currency/rate/${purchase.currency_id}/${purchase.purchase_date}`)
        .then((res) => {
          purchase.currency_rate = res.body.currency_rate;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch currency rate: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    updatePurchases() {
      this.loadingCount++;
      this.$http
        .put(`/select/group_purchases/${this.data.groupKey}`)
        .send({ data: this.sendPurchases })
        .then(() => {
          this.onClose();
        })
        .catch((err) => {
          this.$toast.error(`Failed to update purchases: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    createPurchase() {
      const newPurchase = {
        id: null,
        guid: generateGuid(),
        option_id: null,
        currency_id: 1,
        currency_rate: null,
        purchase_date: this.getToday(),
        purchase_price: '',
        unit_type: 'amount',
        unit_code: 'amount',
        unit_value: '1',
      };
      this.purchases.push(newPurchase);
      this.fetchRate(newPurchase);
      this.$nextTick().then(() => {
        const inputField = document.querySelector('.modal.dropdown-purchases-editor .modal-body table tbody > tr:last-child input:first-child');
        if (inputField) {
          inputField.focus();
        }
      });
    },
    deletePurchase(deletePurchase) {
      if (deletePurchase.id !== null && !confirm('Do you really wish to delete this purchase?')) {
        return;
      }
      this.purchases = this.purchases.filter((purchase) => purchase.guid !== deletePurchase.guid);
    },
    fetchAll() {
      this.fetchGroup();
      this.fetchGroupOptions();
      this.fetchPurchases();
    },
    onSave() {
      this.updatePurchases();
    },
    onShow() {
      this.fetchAll();
    },
    onClose() {
      this.data.showModal = false;
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
</style>
